import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Phone, Menu, X } from "lucide-react";
import { BUSINESS } from "../lib/api";

const links = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/about", label: "About" },
  { to: "/team", label: "Meet the Team" },
  { to: "/contact", label: "Contact" },
];

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "bg-white/90 backdrop-blur-md border-b border-gray-200"
          : "bg-white/70 backdrop-blur-sm border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 md:h-20 flex items-center justify-between gap-4">
        <Link
          to="/"
          data-testid="header-logo-link"
          className="flex items-center gap-3 group"
        >
          <span className="relative inline-flex h-11 w-11 items-center justify-center rounded-xl overflow-hidden ring-1 ring-emerald-900/10 shadow-sm bg-gradient-to-br from-emerald-50 to-white group-hover:shadow-md transition-shadow duration-300">
            <img
              src={BUSINESS.logoUrl}
              alt="Sixty Finance"
              width="88"
              height="88"
              loading="eager"
              className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500"
              style={{ imageRendering: "-webkit-optimize-contrast" }}
            />
          </span>
          <span className="hidden sm:flex flex-col leading-tight">
            <span className="text-[15px] font-bold tracking-tight text-emerald-950">
              Sixty Finance
            </span>
            <span className="text-[11px] text-emerald-700 tracking-wide italic">
              Always by your side
            </span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                  isActive
                    ? "text-emerald-900 bg-emerald-50"
                    : "text-slate-700 hover:text-emerald-800 hover:bg-emerald-50/60"
                }`
              }
              end={l.to === "/"}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <a
            href={BUSINESS.phoneHref}
            data-testid="header-call-btn"
            className="hidden md:inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500 text-emerald-950 font-semibold text-sm shadow-sm hover:bg-emerald-400 hover:scale-105 transition-transform duration-200"
          >
            <Phone className="h-4 w-4" />
            <span className="num">{BUSINESS.phone}</span>
          </a>
          <Link
            to="/quote"
            data-testid="header-quote-btn"
            className="hidden md:inline-flex items-center px-4 py-2 rounded-full bg-emerald-900 text-white font-semibold text-sm hover:bg-emerald-800 transition-colors duration-200"
          >
            Get a free quote
          </Link>
          <a
            href={BUSINESS.phoneHref}
            data-testid="mobile-call-btn"
            className="md:hidden inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-emerald-500 text-emerald-950 text-sm font-semibold"
          >
            <Phone className="h-4 w-4" />
            Call
          </a>
          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
            className="lg:hidden inline-flex items-center justify-center h-10 w-10 rounded-lg text-emerald-900 hover:bg-emerald-50"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden border-t border-gray-200 bg-white"
        >
          <nav className="px-4 py-3 flex flex-col gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `px-3 py-3 rounded-lg text-sm font-medium ${
                    isActive
                      ? "text-emerald-900 bg-emerald-50"
                      : "text-slate-700 hover:bg-emerald-50/60"
                  }`
                }
                end={l.to === "/"}
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/quote"
              data-testid="mobile-quote-btn"
              className="mt-2 inline-flex items-center justify-center px-4 py-3 rounded-full bg-emerald-900 text-white font-semibold text-sm"
            >
              Get a free quote
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};
